import React from 'react';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { FileText, Building2, Clock, IndianRupee, Mail, Phone, MapPin, Scale, Download, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';

export const RTI = () => {
  return (
    <>
      <Header />
      <main className="min-h-screen bg-white" role="main" aria-label="Right to Information page">
        {/* Hero */}
        <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 mb-6">
              <Scale className="h-4 w-4 text-amber-400" />
              <span className="text-xs font-semibold text-amber-400 tracking-wider uppercase">Statutory Compliance</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="rti-title">Right to Information (RTI)</h1>
            <p className="text-base text-white/60 max-w-2xl mx-auto leading-relaxed">
              In compliance with the Right to Information Act, 2005, Ariskool is committed to transparency and accountability in the discharge of its functions.
            </p>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 py-12" data-testid="rti-content">
          {/* Section 4(1)(b) Disclosures */}
          <section className="mb-12" aria-labelledby="suo-motu-heading">
            <h2 id="suo-motu-heading" className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <FileText className="h-6 w-6 text-teal-600" />
              Suo Motu Disclosure under Section 4(1)(b)
            </h2>

            <div className="space-y-6">
              <div className="p-5 rounded-xl bg-slate-50 border border-slate-100">
                <h3 className="font-semibold text-slate-900 mb-2">(i) Organisation & Functions</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Ariskool is a SaaS-based Leave Management System designed for educational institutions across India. The platform enables schools to manage staff leave applications, approvals, policy configuration, holiday calendars, and HR workflows through a secure, multi-tenant cloud infrastructure.
                </p>
              </div>

              <div className="p-5 rounded-xl bg-slate-50 border border-slate-100">
                <h3 className="font-semibold text-slate-900 mb-2">(ii) Powers and Duties of Officers</h3>
                <ul className="text-sm text-slate-600 space-y-1.5 leading-relaxed">
                  <li><strong>Platform Administrator:</strong> Overall system administration, tenant management, compliance oversight, data governance.</li>
                  <li><strong>School Administrator:</strong> School-level user management, leave policy configuration, approval workflows, reporting.</li>
                  <li><strong>HR Manager:</strong> Leave request processing, employee records management, policy enforcement.</li>
                  <li><strong>Data Protection Officer (DPO):</strong> Oversees data privacy, CERT-In compliance, breach notification, and RTI responses.</li>
                </ul>
              </div>

              <div className="p-5 rounded-xl bg-slate-50 border border-slate-100">
                <h3 className="font-semibold text-slate-900 mb-2">(iii) Procedure for Decision Making</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Leave applications follow a hierarchical approval workflow: Teacher &rarr; HR Manager &rarr; School Administrator. Policy changes require School Administrator approval. Platform-level decisions are taken by the Chief Operating Officer in consultation with technical and legal teams.
                </p>
              </div>

              <div className="p-5 rounded-xl bg-slate-50 border border-slate-100">
                <h3 className="font-semibold text-slate-900 mb-2">(iv) Norms for Discharge of Functions</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  All operations comply with: The Information Technology Act, 2000 (amended 2008); CERT-In Cyber Security Directions (April 2022); NIST Cybersecurity Framework; DPDP Act, 2023 (Digital Personal Data Protection); and applicable state education board regulations.
                </p>
              </div>

              <div className="p-5 rounded-xl bg-slate-50 border border-slate-100">
                <h3 className="font-semibold text-slate-900 mb-2">(v) Categories of Documents</h3>
                <ul className="text-sm text-slate-600 space-y-1 leading-relaxed list-disc ml-5">
                  <li>Terms of Service and Privacy Policy</li>
                  <li>Data Processing Agreements (DPA)</li>
                  <li>Security and Compliance documentation</li>
                  <li>Service Level Agreements (SLA)</li>
                  <li>Incident Response and Data Breach Notification Policy</li>
                  <li>User guides and technical documentation</li>
                </ul>
              </div>
            </div>
          </section>

          {/* PIO & APIO Details */}
          <section className="mb-12" aria-labelledby="pio-heading">
            <h2 id="pio-heading" className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <Building2 className="h-6 w-6 text-teal-600" />
              Public Information Officers
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-teal-100">
                <CardContent className="p-6">
                  <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-teal-50 text-teal-700 text-xs font-bold mb-4">PIO</div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3" data-testid="pio-name">Public Information Officer</h3>
                  <div className="space-y-2 text-sm text-slate-600">
                    <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /> rti@ariskool.in</p>
                    <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> +91-80-XXXX-XXXX</p>
                    <p className="flex items-start gap-2"><MapPin className="h-4 w-4 text-slate-400 mt-0.5" /> Ariskool Technologies Pvt. Ltd., Koramangala, Bangalore, Karnataka 560034</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-amber-100">
                <CardContent className="p-6">
                  <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-amber-50 text-amber-700 text-xs font-bold mb-4">APIO</div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3" data-testid="apio-name">Appellate Authority</h3>
                  <div className="space-y-2 text-sm text-slate-600">
                    <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /> appellate@ariskool.in</p>
                    <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> +91-80-XXXX-XXXX</p>
                    <p className="flex items-start gap-2"><MapPin className="h-4 w-4 text-slate-400 mt-0.5" /> Ariskool Technologies Pvt. Ltd., Koramangala, Bangalore, Karnataka 560034</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Fee Structure */}
          <section className="mb-12" aria-labelledby="fee-heading">
            <h2 id="fee-heading" className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <IndianRupee className="h-6 w-6 text-teal-600" />
              Fee Structure
            </h2>
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <table className="w-full" role="table" aria-label="RTI fee structure">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider" scope="col">Description</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider" scope="col">Fee (INR)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr><td className="px-5 py-3 text-sm text-slate-700">Application fee</td><td className="px-5 py-3 text-sm text-slate-700 text-right font-mono">10.00</td></tr>
                  <tr className="bg-slate-50/50"><td className="px-5 py-3 text-sm text-slate-700">Per page (A4 / photocopy)</td><td className="px-5 py-3 text-sm text-slate-700 text-right font-mono">2.00</td></tr>
                  <tr><td className="px-5 py-3 text-sm text-slate-700">Per page (larger size)</td><td className="px-5 py-3 text-sm text-slate-700 text-right font-mono">Actual cost</td></tr>
                  <tr className="bg-slate-50/50"><td className="px-5 py-3 text-sm text-slate-700">Inspection of records (first hour)</td><td className="px-5 py-3 text-sm text-slate-700 text-right font-mono">Free</td></tr>
                  <tr><td className="px-5 py-3 text-sm text-slate-700">Inspection (per subsequent hour)</td><td className="px-5 py-3 text-sm text-slate-700 text-right font-mono">5.00</td></tr>
                  <tr className="bg-slate-50/50"><td className="px-5 py-3 text-sm text-slate-700">CD / Floppy / Digital media</td><td className="px-5 py-3 text-sm text-slate-700 text-right font-mono">50.00</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400 mt-3">* BPL applicants are exempt from fees. Payment via Demand Draft / Indian Postal Order payable to Ariskool Technologies Pvt. Ltd.</p>
          </section>

          {/* How to File */}
          <section className="mb-12" aria-labelledby="howto-heading">
            <h2 id="howto-heading" className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <Clock className="h-6 w-6 text-teal-600" />
              How to File an RTI Request
            </h2>
            <div className="space-y-4">
              {[
                { step: 1, title: 'Draft Your Application', desc: 'Write a clear and specific request in English or Hindi. Include your name, address, and the information sought.' },
                { step: 2, title: 'Attach Fee Payment', desc: 'Attach a DD/IPO of Rs. 10/- payable to "Ariskool Technologies Pvt. Ltd." (BPL cardholders exempted — attach proof).' },
                { step: 3, title: 'Send to PIO', desc: 'Send your application via registered post or email to the PIO address listed above.' },
                { step: 4, title: 'Receive Response', desc: 'The PIO will respond within 30 days. If information concerns life/liberty, response will be given within 48 hours.' },
                { step: 5, title: 'First Appeal', desc: 'If unsatisfied, file a first appeal to the Appellate Authority within 30 days of receipt/expiry of response time.' },
                { step: 6, title: 'Second Appeal', desc: 'File a second appeal with the Central/State Information Commission within 90 days of the first appeal decision.' },
              ].map(item => (
                <div key={item.step} className="flex gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="h-8 w-8 rounded-lg bg-teal-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{item.step}</div>
                  <div>
                    <h4 className="font-semibold text-slate-900 text-sm">{item.title}</h4>
                    <p className="text-sm text-slate-600 mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Compliance Frameworks */}
          <section className="mb-12" aria-labelledby="compliance-heading">
            <h2 id="compliance-heading" className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <Scale className="h-6 w-6 text-teal-600" />
              Applicable Compliance Frameworks
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { name: 'RTI Act, 2005', desc: 'Right to Information compliance with proactive disclosures', color: 'teal' },
                { name: 'CERT-In Directions', desc: 'Mandatory cyber incident reporting within 6 hours, audit logging, NTP sync', color: 'red' },
                { name: 'NIST CSF', desc: 'Identify, Protect, Detect, Respond, Recover framework implementation', color: 'blue' },
                { name: 'WCAG 2.1 AA', desc: 'Web Content Accessibility Guidelines for inclusive access', color: 'purple' },
                { name: 'DPDP Act, 2023', desc: 'Digital Personal Data Protection with consent management', color: 'amber' },
                { name: 'IT Act, 2000', desc: 'Information Technology Act compliance for electronic records', color: 'emerald' },
              ].map((fw) => (
                <div key={fw.name} className={`p-4 rounded-xl border border-${fw.color}-100 bg-${fw.color}-50/30`}>
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">{fw.name}</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">{fw.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Data Retention */}
          <section className="mb-12 p-6 rounded-xl bg-amber-50 border border-amber-200" aria-labelledby="retention-heading">
            <h2 id="retention-heading" className="text-lg font-bold text-amber-900 mb-3">Data Retention Policy</h2>
            <ul className="space-y-2 text-sm text-amber-800">
              <li><strong>Active data:</strong> Retained for the duration of subscription + 90 days grace period</li>
              <li><strong>Audit logs:</strong> Retained for a minimum of 180 days as per CERT-In requirements</li>
              <li><strong>Deleted accounts:</strong> Data anonymized within 30 days of account deletion request</li>
              <li><strong>Backups:</strong> Retained for 30 days, automatically purged thereafter</li>
              <li><strong>RTI records:</strong> Maintained for 5 years from the date of disposal</li>
            </ul>
          </section>

          {/* External Links */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Useful Links</h2>
            <div className="flex flex-wrap gap-3">
              {[
                { label: 'RTI Online Portal', url: 'https://rtionline.gov.in/' },
                { label: 'CIC Website', url: 'https://cic.gov.in/' },
                { label: 'CERT-In', url: 'https://www.cert-in.org.in/' },
                { label: 'MeitY', url: 'https://www.meity.gov.in/' },
              ].map(link => (
                <a
                  key={link.label}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-100 text-sm text-slate-700 hover:bg-slate-200 transition-colors"
                  aria-label={`Visit ${link.label} (opens in new tab)`}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  {link.label}
                </a>
              ))}
            </div>
          </section>

          <p className="text-xs text-slate-400 text-center border-t border-slate-100 pt-6">
            Last updated: February 2026. This page is maintained in compliance with Section 4 of the RTI Act, 2005.
          </p>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default RTI;
